/**
 * The one shared timestamp formatter. Every display defaults to 'UTC' when no
 * timezone is passed, so a call site that hasn't been wired to
 * `useOrgTimezone()` yet (or an org that never set one) renders exactly what it
 * always has — this is additive, not a behavior change.
 *
 * Every formatter below that can be ambiguous once an org is NOT on UTC (i.e.
 * everything except `formatClock`, used only for dense chart-axis ticks where a
 * repeated suffix would be pure clutter) appends the zone's short name — but
 * ONLY when the timezone isn't UTC, so the UTC-default case stays byte-for-byte
 * identical to what every existing caller/test already expects, and the label
 * appears exactly when it stops being safe to assume.
 */
export const DEFAULT_TIMEZONE = 'UTC';

const BASE: Intl.DateTimeFormatOptions = { hour12: false };

export function isUtc(timezone: string): boolean {
  return timezone.trim().toUpperCase() === 'UTC';
}

/**
 * `Intl.DateTimeFormat` instances, kept rather than rebuilt per call.
 *
 * Constructing one is expensive — measured on this repo's Node 22 (full ICU),
 * formatting a clock time went from 0.5 µs with raw string slicing to 30 µs for
 * UTC and 67 µs for a named zone, essentially all of it construction. That is
 * invisible for a caption rendered once and is not invisible on the COP: the
 * alert panel can hold 100 rows, `useWebSocket` re-renders the whole tree on
 * every telemetry frame, and at the 50-drone/1 Hz target that arithmetic reaches
 * a third of a second per second of main thread — shared with MapLibre.
 *
 * Keyed by timezone because that is the only option that varies per call. The
 * map is unbounded by type and bounded in fact: the keys come from an org
 * setting chosen from a curated list, not from user input.
 */
const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

function zoneFormatter(timezone: string): Intl.DateTimeFormat {
  let formatter = zoneFormatters.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' });
    zoneFormatters.set(timezone, formatter);
  }
  return formatter;
}

/**
 * The instant, or null when there isn't one.
 *
 * Every formatter here is fed values that crossed the wire, and an unparseable
 * one must degrade to a placeholder rather than reach `Intl` — `toLocaleString`
 * answers an invalid date with the literal string "Invalid Date" while
 * `formatToParts` THROWS, so without this the same bad timestamp either prints
 * nonsense or white-screens the page, decided by whether the org happens to be
 * on UTC.
 */
function asDate(value: string | number | Date): Date | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "GMT+3" / "UTC" / "EST" — for a caption that spells out the zone next to a value
 *  formatted separately (e.g. "Generated <date> <zoneAbbreviation>").
 *
 *  Note this is the SHORT name, which CLDR only has for some zones: `America/New_York`
 *  gives "EST", while `Asia/Jerusalem` has none and falls back to the offset "GMT+3".
 *  Both are unambiguous, which is what this is for. */
export function zoneAbbreviation(value: string | number | Date, timezone: string = DEFAULT_TIMEZONE): string {
  const date = asDate(value);
  if (!date) return timezone;
  const part = zoneFormatter(timezone)
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? timezone;
}

/** Appends ` <ZONE>` unless `timezone` is UTC, in which case `base` is returned
 *  unchanged — the byte-identical-by-default rule every formatter below follows. */
function withZoneLabel(base: string, value: string | number | Date, timezone: string): string {
  return isUtc(timezone) ? base : `${base} ${zoneAbbreviation(value, timezone)}`;
}

/** "Sep 6, 2026", or "Sep 6, 2026 IDT" once the org isn't on UTC. */
export function formatDateOnly(value: string | number | Date, timezone: string = DEFAULT_TIMEZONE): string {
  const base = new Date(value).toLocaleDateString('en-US', {
    ...BASE,
    timeZone: timezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return withZoneLabel(base, value, timezone);
}

/**
 * For a value that is a calendar DATE with no time-of-day (a Postgres `DATE`
 * column, e.g. a pilot's Part 107 expiry) — always reads the UTC calendar
 * fields, ignoring `timezone` entirely.
 *
 * A `DATE` has no time component, so the driver/JSON round-trip represents it as
 * UTC midnight of that day. Formatting that instant in a non-UTC zone shifts it
 * to the wrong calendar day for any zone behind UTC (the entire continental US):
 * an expiry stored as 2027-03-15 would render as "Mar 14, 2027" for an org set
 * to America/Los_Angeles. There is no correct org-zone reading of a bare date —
 * only a wrong one — so this intentionally never takes a `timezone` argument.
 */
export function formatCalendarDate(value: string | number | Date): string {
  return new Date(value).toLocaleDateString('en-US', {
    ...BASE,
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** "Sep 6, 14:32", or "Sep 6, 14:32 IDT" once the org isn't on UTC. */
export function formatDateTime(value: string | number | Date, timezone: string = DEFAULT_TIMEZONE): string {
  const base = new Date(value).toLocaleString('en-US', {
    ...BASE,
    timeZone: timezone,
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return withZoneLabel(base, value, timezone);
}

/** "Sep 6, 2026, 14:32", or "... IDT" once the org isn't on UTC. */
export function formatDateTimeFull(value: string | number | Date, timezone: string = DEFAULT_TIMEZONE): string {
  const base = new Date(value).toLocaleString('en-US', {
    ...BASE,
    timeZone: timezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return withZoneLabel(base, value, timezone);
}

/**
 * "14:32:07" — deliberately never labeled with a zone, unlike every other
 * formatter here. The one caller (the flight-detail telemetry chart's X-axis)
 * repeats this on every tick; a per-tick suffix would be dense-chart clutter,
 * not clarity, and a chart already reads as one continuous timeline in
 * whichever single zone the page is using.
 */
export function formatClock(value: string | number | Date, timezone: string = DEFAULT_TIMEZONE): string {
  return clockFormatter(timezone).format(new Date(value));
}

/** Cached for the reason `zoneFormatter` is: this one is called per chart tick
 *  and, through `formatClockWithZone`, once per alert row per render. */
const clockFormatters = new Map<string, Intl.DateTimeFormat>();

function clockFormatter(timezone: string): Intl.DateTimeFormat {
  let formatter = clockFormatters.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      ...BASE,
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    clockFormatters.set(timezone, formatter);
  }
  return formatter;
}

/**
 * "14:32Z" for UTC (byte-identical to the literal `Z` suffix every caller here
 * used to hardcode) or "14:32 IDT" otherwise — for a stamp whose old format
 * already ended in a manual "Z" rather than a bare number, so preserving that
 * exact shape when the org hasn't opted into a different zone matters more than
 * usual: at least one caller (`MissionCoveragePanel.tsx`'s collapsed
 * "REPLACEMENT DUE" chip) documents a hard character budget for this exact
 * string on a 360px surface.
 */
export function formatStampWithZone(value: string | number | Date, timezone: string = DEFAULT_TIMEZONE): string {
  const base = new Date(value).toLocaleString('en-US', {
    ...BASE,
    timeZone: timezone,
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return isUtc(timezone) ? `${base}Z` : `${base} ${zoneAbbreviation(value, timezone)}`;
}

/**
 * "13:45:02 GMT+3", or "10:45:02 UTC" on a UTC org — a running clock, always
 * carrying its zone.
 *
 * The zone label is unconditional here, unlike `withZoneLabel` above. A clock is
 * read at a glance and acted on, and an operator seeing a bare `13:45:02` cannot
 * tell whether the battery window they are counting against is in their own time
 * or in Zulu — three hours apart in Israel. The UTC form keeps the exact ` UTC`
 * suffix the top bar has always rendered, so a UTC deployment stays
 * byte-identical to before this existed.
 */
export function formatClockWithZone(
  value: string | number | Date,
  timezone: string = DEFAULT_TIMEZONE,
  /**
   * What a UTC org sees after the time. Defaults to the top bar's long form.
   *
   * A parameter rather than one house answer because the two callers arrived
   * with DIFFERENT UTC shapes and this module's contract is that a UTC org keeps
   * rendering byte-for-byte what it did: the clock has always said ` UTC`, the
   * alert bell has always said `Z`. Both gain the zone name once the org is not
   * on UTC, which is the part that actually changed.
   */
  utcSuffix: ' UTC' | 'Z' = ' UTC',
): string {
  const date = asDate(value);
  if (!date) return '--:--:--';
  const base = formatClock(date, timezone);
  return isUtc(timezone) ? `${base}${utcSuffix}` : `${base} ${zoneAbbreviation(date, timezone)}`;
}

/**
 * "2026-09-09 13:45 GMT+3", or "2026-09-09 10:45Z" on a UTC org — the sortable,
 * fixed-width shape a dense table needs, in the org's zone.
 *
 * `sv-SE` is used because its locale format IS the ISO one; assembling the parts
 * by hand is the version that gets a month off by one. `formatDateTime` would be
 * the house style, but it renders "Sep 9, 2026, 13:45", which is both wider and
 * no longer column-aligned — in a table an operator scans down a column, and that
 * costs more than the consistency gains.
 */
export function formatIsoLike(value: string | number | Date, timezone: string = DEFAULT_TIMEZONE): string {
  const date = asDate(value);
  if (!date) return '------- --:--';
  const base = isoLikeFormatter(timezone).format(date);
  return isUtc(timezone) ? `${base}Z` : `${base} ${zoneAbbreviation(date, timezone)}`;
}

/** Same construction cost as `zoneFormatter` above, same reason for keeping it. */
const isoLikeFormatters = new Map<string, Intl.DateTimeFormat>();

function isoLikeFormatter(timezone: string): Intl.DateTimeFormat {
  let formatter = isoLikeFormatters.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('sv-SE', {
      ...BASE,
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    isoLikeFormatters.set(timezone, formatter);
  }
  return formatter;
}
